import { useState, useRef, useEffect, useCallback } from "react";

const ITEM_HEIGHT = 48; // Height of each item in pixels
const VISIBLE_ITEMS = 3; // Number of visible items (odd number for center selection)

const ScrollColumn = ({ items, selectedIndex, onSelect, label }) => {
  const containerRef = useRef(null);
  const isDragging = useRef(false);
  const startY = useRef(0);
  const startScroll = useRef(0);

  // Scroll to selected item on mount and when selectedIndex changes
  useEffect(() => {
    if (containerRef.current) {
      const scrollTop = selectedIndex * ITEM_HEIGHT;
      containerRef.current.scrollTop = scrollTop;
    }
  }, [selectedIndex]);

  const handleScroll = useCallback(() => {
    if (containerRef.current && !isDragging.current) {
      const scrollTop = containerRef.current.scrollTop;
      const newIndex = Math.round(scrollTop / ITEM_HEIGHT);
      const clampedIndex = Math.max(0, Math.min(newIndex, items.length - 1));
      if (clampedIndex !== selectedIndex) {
        onSelect(clampedIndex);
      }
    }
  }, [items.length, selectedIndex, onSelect]);

  const snapToItem = useCallback(() => {
    if (containerRef.current) {
      const scrollTop = containerRef.current.scrollTop;
      const targetIndex = Math.round(scrollTop / ITEM_HEIGHT);
      const clampedIndex = Math.max(0, Math.min(targetIndex, items.length - 1));
      containerRef.current.scrollTo({
        top: clampedIndex * ITEM_HEIGHT,
        behavior: 'smooth'
      });
      onSelect(clampedIndex);
    }
  }, [items.length, onSelect]);

  const handleTouchStart = (e) => {
    isDragging.current = true;
    startY.current = e.touches[0].clientY;
    startScroll.current = containerRef.current?.scrollTop || 0;
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current || !containerRef.current) return;
    const deltaY = startY.current - e.touches[0].clientY;
    containerRef.current.scrollTop = startScroll.current + deltaY;
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
    snapToItem();
  };

  const handleMouseDown = (e) => {
    isDragging.current = true;
    startY.current = e.clientY;
    startScroll.current = containerRef.current?.scrollTop || 0;
    e.preventDefault();
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current || !containerRef.current) return;
    const deltaY = startY.current - e.clientY;
    containerRef.current.scrollTop = startScroll.current + deltaY;
  }, []);

  const handleMouseUp = useCallback(() => {
    if (isDragging.current) {
      isDragging.current = false;
      snapToItem();
    }
  }, [snapToItem]);

  // Add global mouse listeners for dragging
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return (
    <div className="flex flex-col items-center">
      <div className="relative h-[144px] w-20 overflow-hidden">
        {/* Gradient overlays for 3D effect */}
        <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-[#121212] to-transparent z-10 pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-[#121212] to-transparent z-10 pointer-events-none" />
        
        {/* Selection highlight */}
        <div className="absolute top-12 left-0 right-0 h-12 border-y border-cyan-500/50 bg-cyan-500/5 pointer-events-none z-5" />
        
        {/* Scrollable container */}
        <div
          ref={containerRef}
          className="h-full overflow-y-scroll scrollbar-hide"
          style={{
            scrollSnapType: 'y mandatory',
            paddingTop: ITEM_HEIGHT,
            paddingBottom: ITEM_HEIGHT,
          }}
          onScroll={handleScroll}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
        >
          {items.map((item, index) => (
            <div
              key={`${label}-${index}`}
              className={`h-12 flex items-center justify-center cursor-pointer transition-all duration-150 select-none
                ${index === selectedIndex 
                  ? 'text-white text-3xl font-oswald font-bold' 
                  : 'text-gray-500 text-xl font-oswald'
                }
              `}
              style={{ scrollSnapAlign: 'center' }}
              onClick={() => {
                onSelect(index);
                if (containerRef.current) {
                  containerRef.current.scrollTo({
                    top: index * ITEM_HEIGHT,
                    behavior: 'smooth'
                  });
                }
              }}
            >
              {item}
            </div>
          ))}
        </div>
      </div>
      <span className="text-xs text-gray-500 mt-1 uppercase tracking-wider">{label}</span>
    </div>
  );
};

const RollingTimePicker = ({ value = "09:00", onChange }) => {
  // Parse 24h time to 12h format
  const parse24hTo12h = (time24) => {
    const [h24, m] = time24.split(':').map(Number);
    const hour12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
    const period = h24 >= 12 ? 'PM' : 'AM';
    return { hour: hour12, minute: m, period };
  };

  // Convert 12h to 24h format
  const convert12hTo24h = (hour12, minute, period) => {
    let hour24 = hour12;
    if (period === 'AM') {
      hour24 = hour12 === 12 ? 0 : hour12;
    } else {
      hour24 = hour12 === 12 ? 12 : hour12 + 12;
    }
    return `${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  };

  const { hour, minute, period } = parse24hTo12h(value);

  // Hours 1-12
  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
  // Minutes in 15-min increments
  const minutes = ['00', '15', '30', '45'];
  // AM/PM
  const periods = ['AM', 'PM'];

  const hourIndex = hour - 1;
  const minuteIndex = minutes.indexOf(String(minute).padStart(2, '0'));
  const periodIndex = periods.indexOf(period);

  const handleHourChange = (index) => {
    const newHour = index + 1;
    const newTime = convert12hTo24h(newHour, minute, period);
    onChange(newTime);
  };

  const handleMinuteChange = (index) => {
    const newMinute = parseInt(minutes[index]);
    const newTime = convert12hTo24h(hour, newMinute, period);
    onChange(newTime);
  };

  const handlePeriodChange = (index) => {
    const newPeriod = periods[index];
    const newTime = convert12hTo24h(hour, minute, newPeriod);
    onChange(newTime);
  };

  return (
    <div 
      className="flex items-center justify-center gap-1 bg-[#121212] rounded-lg p-4 border border-white/10"
      data-testid="rolling-time-picker"
    >
      <ScrollColumn
        items={hours}
        selectedIndex={hourIndex}
        onSelect={handleHourChange}
        label="Hour"
      />
      
      <div className="text-3xl font-oswald text-gray-500 self-center mb-6">:</div>
      
      <ScrollColumn
        items={minutes}
        selectedIndex={minuteIndex >= 0 ? minuteIndex : 0}
        onSelect={handleMinuteChange}
        label="Min"
      />
      
      <div className="w-2" />
      
      <ScrollColumn
        items={periods}
        selectedIndex={periodIndex >= 0 ? periodIndex : 0}
        onSelect={handlePeriodChange}
        label=""
      />
    </div>
  );
};

export default RollingTimePicker;
